import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  MenuItem,
  TextField,
  Divider,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import ParentLayout from './Layout';
import { listEnrollments } from '../api/enrollments';
import { listSessions } from '../api/sessions';
import { listLessons } from '../api/lessons';
import { listAssessments } from '../api/assessments';
import { listSubmissions } from '../api/submissions';
import { getCalendarToken } from '../api/users';
import analyticsApi from '../api/analytics';

const ParentDashboard = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [sessions, setSessions] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportLink, setReportLink] = useState(null);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');

  const getPhaseForGrade = (grade) => {
    const num = Number(grade);
    if (!Number.isFinite(num)) return 'Unknown';
    if (num >= 1 && num <= 3) return 'Foundation';
    if (num >= 4 && num <= 6) return 'Intermediate';
    if (num >= 7 && num <= 9) return 'Senior';
    return 'FET';
  };
  const currentEnrollment = enrollments.find((enr) => enr.studentId?._id === selectedLearner);
  const placement = currentEnrollment?.sectionId
    ? `${currentEnrollment.sectionId.year}-G${currentEnrollment.sectionId.grade}${currentEnrollment.sectionId.section}`
    : currentEnrollment?.year && currentEnrollment?.grade
      ? `${currentEnrollment.year}-G${currentEnrollment.grade}`
      : 'Not assigned';

  const subjectNames = (currentEnrollment?.subjects || [])
    .map((s) => s?.name || s)
    .filter(Boolean);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const enr = await listEnrollments({ status: 'active' });
        setEnrollments(enr || []);
        const first = enr?.[0]?.studentId?._id || '';
        setSelectedLearner(first);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load learners');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedLearner) return;
      try {
        setLoading(true);
        setCalendarFeedUrl('');
        const [sess, lessonData, assessmentData, submissionData] = await Promise.all([
          listSessions({ studentId: selectedLearner, status: 'scheduled' }),
          listLessons({ availableOnly: true }),
          listAssessments({}),
          listSubmissions({}),
        ]);
        setSessions(Array.isArray(sess) ? sess.slice(0, 5) : []);
        setLessons(Array.isArray(lessonData) ? lessonData.slice(0, 5) : []);
        setAssessments(Array.isArray(assessmentData) ? assessmentData.slice(0, 5) : []);
        const submissionMap = {};
        (submissionData || []).forEach((s) => { submissionMap[s.assessmentId] = s; });
        setSubmissions(submissionMap);
        const blob = await analyticsApi.getTermReport({ studentId: selectedLearner });
        if (blob) {
          const url = URL.createObjectURL(blob);
          setReportLink(url);
        }
        const feed = await getCalendarToken(selectedLearner);
        setCalendarFeedUrl(feed?.feedUrl || '');
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedLearner]);

  const handleCopyFeed = async () => {
    if (!calendarFeedUrl) return;
    try {
      await navigator.clipboard.writeText(calendarFeedUrl);
    } catch {}
  };

  const learnerOptions = enrollments.map((enr) => ({
    id: enr.studentId?._id,
    label: `${enr.studentId?.name || ''} ${enr.studentId?.surname || ''} (${enr.grade ? `G${enr.grade}` : ''})`,
  }));

  return (
    <ParentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Parent Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor your learner's timetable, lessons, assessments, and submissions.
            </Typography>
          </Box>

          {(loading) && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Select learner"
            value={selectedLearner}
            onChange={(e) => setSelectedLearner(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {learnerOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="My Child" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">Class placement</Typography>
                        <Typography fontWeight={700}>{placement}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">Language choices</Typography>
                        <Typography fontWeight={700}>
                          {currentEnrollment?.languageSelection?.homeLanguage || '-'} /{' '}
                          {currentEnrollment?.languageSelection?.firstAdditionalLanguage || '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">CAPS phase subjects</Typography>
                        <Typography fontWeight={700}>
                          {getPhaseForGrade(currentEnrollment?.grade)} - {subjectNames.length} subjects
                        </Typography>
                      </Stack>
                    </Grid>
                    {subjectNames.length > 0 && (
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {subjectNames.map((name) => (
                            <Chip key={name} size="small" label={name} />
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Upcoming timetable" avatar={<EventAvailableIcon color="primary" />} />
                <CardContent>
                  <Stack spacing={1}>
                    {sessions.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No upcoming sessions.
                      </Typography>
                    )}
                    {sessions.map((session) => (
                      <Stack key={session._id} spacing={0.5} divider={<Divider flexItem />}>
                        <Typography fontWeight={700}>{session.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {session.startTime ? new Date(session.startTime).toLocaleString() : ''}
                        </Typography>
                        <Chip size="small" label={session.sectionId ? session.sectionId.section : 'General'} />
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Lessons & resources" avatar={<SchoolIcon color="primary" />} />
                <CardContent>
                  <Stack spacing={1}>
                    {lessons.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No lessons published yet.</Typography>
                    )}
                    {lessons.map((lesson) => (
                      <Stack key={lesson._id} spacing={0.5} divider={<Divider flexItem />}>
                        <Typography fontWeight={700}>{lesson.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lesson.description || 'No description'}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" label={`G${lesson.grade}`} />
                          <Chip size="small" label={`Files: ${lesson.files?.length || 0}`} />
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Assessments & submissions" avatar={<AssignmentIcon color="primary" />} />
                <CardContent>
                  <Grid container spacing={2}>
                    {assessments.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">No assessments yet.</Typography>
                      </Grid>
                    )}
                    {assessments.map((assessment) => {
                      const existing = submissions[assessment._id];
                      return (
                        <Grid item xs={12} md={4} key={assessment._id}>
                          <Stack spacing={0.5} divider={<Divider flexItem />}>
                            <Typography fontWeight={700}>{assessment.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Due: {assessment.dueDate ? new Date(assessment.dueDate).toLocaleString() : 'TBC'}
                            </Typography>
                            <Chip size="small" label={assessment.type?.toUpperCase()} />
                            {existing ? (
                              <Chip size="small" color="success" label={`Submitted (${existing.status})`} />
                            ) : (
                              <Chip size="small" color="warning" label="Not submitted" />
                            )}
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Reports & documents" avatar={<DownloadIcon color="primary" />} />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Download term reports and other shared documents.
                    </Typography>
                    {reportLink ? (
                      <Button variant="outlined" href={reportLink} target="_blank" rel="noreferrer" startIcon={<DownloadIcon />}>
                        Download term report
                      </Button>
                    ) : (
                      <Chip label="Report not ready" size="small" />
                    )}
                    <Stack spacing={1} sx={{ pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Calendar feed for selected learner
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <TextField
                          value={calendarFeedUrl || 'Generating feed link...'}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                        <Button variant="outlined" onClick={handleCopyFeed} disabled={!calendarFeedUrl}>
                          Copy link
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </ParentLayout>
  );
};

export default ParentDashboard;
